// client/src/coms/views/canvas_parts/canvas_room_view_parts/paintBrushUtils.js

import { normalisePhysicalPaintSettings } from '../../../canvas/paint-engine/integration/physicalPaintSettings';

const PAINT_HEIGHT_PHASE_COUNT = 4000;
const PAINT_HEIGHT_MAX_UNITS = 4000;
const PAINT_HEIGHT_UNITS_PER_PIXEL = 1;

const DEFAULT_PAINT_SETTINGS = {
  tool: 'tube',
  view: 'final',
  paintColor: '#d52b24',
  toolSize: 70,
  bristleCount: 32,
  bristleThickness: 35,
  bristleLength: 40,
  brushPressure: 100,
  infinitePaint: true,
  sideDeposits: true,
  liquid: 0,
  stickiness: 82,
  gloss: 78,
  lightAngle: 315,
  lightElevation: 35,
};

let paintSettings = { ...DEFAULT_PAINT_SETTINGS };
let paintDebugViewMode = 'final';
let brushLoadVolume = 0;
let brushLoadColor = DEFAULT_PAINT_SETTINGS.paintColor;
let brushLoadRevision = 0;
let brushLoading = false;
let brushLoadingStartedAt = 0;

function clampNumber(value, min, max, fallback) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.max(min, Math.min(max, numeric));
}

function normaliseHexColor(value, fallback = '#d52b24') {
  const raw = String(value || '').trim();
  if (/^#[0-9a-f]{6}$/i.test(raw)) return raw.toLowerCase();
  if (/^#[0-9a-f]{3}$/i.test(raw)) {
    return `#${raw[1]}${raw[1]}${raw[2]}${raw[2]}${raw[3]}${raw[3]}`.toLowerCase();
  }
  return fallback;
}

function normalisePaintDebugViewMode(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'color' || raw === 'colour') return 'color';
  if (raw === 'heightmap' || raw === 'height') return 'heightmap';
  return 'final';
}

function normalisePaintToolName(value, fallback = DEFAULT_PAINT_SETTINGS.tool) {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'tube' || raw === 'painttube' || raw === 'paint-tube' || raw === 'paint_tube' || raw === 'paint tube') return 'tube';
  if (raw === 'brush' || raw === 'paintbrush' || raw === 'paint-brush' || raw === 'paint_brush' || raw === 'paint brush') return 'brush';
  if (raw === 'knife' || raw === 'paletteknife' || raw === 'palette-knife' || raw === 'palette_knife' || raw === 'palette knife') return 'knife';
  return fallback;
}

function updateLoadingState() {
  const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
  if (brushLoading) {
    const elapsed = Math.max(0, now - brushLoadingStartedAt);
    const nextVolume = Math.min(1, brushLoadVolume + elapsed / 1800);
    if (nextVolume !== brushLoadVolume) brushLoadRevision += 1;
    brushLoadVolume = nextVolume;
    brushLoadColor = paintSettings.paintColor;
    brushLoadingStartedAt = now;
  }
}

export function normalisePaintSettings(value = {}) {
  const source = value && typeof value === 'object' ? value : {};
  const nextTool = normalisePaintToolName(source.tool, paintSettings.tool || DEFAULT_PAINT_SETTINGS.tool);

  return {
    tool: nextTool,
    view: normalisePaintDebugViewMode(source.view || source.paintDebugViewMode || paintDebugViewMode),
    paintColor: normaliseHexColor(source.paintColor, paintSettings.paintColor || DEFAULT_PAINT_SETTINGS.paintColor),
    toolSize: clampNumber(source.toolSize, 4, 220, paintSettings.toolSize || DEFAULT_PAINT_SETTINGS.toolSize),
    bristleCount: Math.round(clampNumber(source.bristleCount, 1, 250, paintSettings.bristleCount || DEFAULT_PAINT_SETTINGS.bristleCount)),
    bristleThickness: Math.round(clampNumber(source.bristleThickness, 1, 100, paintSettings.bristleThickness || DEFAULT_PAINT_SETTINGS.bristleThickness)),
    bristleLength: Math.round(clampNumber(source.bristleLength, 1, 300, paintSettings.bristleLength || DEFAULT_PAINT_SETTINGS.bristleLength)),
    brushPressure: Math.round(clampNumber(source.brushPressure, 1, 100, paintSettings.brushPressure || DEFAULT_PAINT_SETTINGS.brushPressure)),
    infinitePaint: source.infinitePaint !== false,
    sideDeposits: source.sideDeposits !== false,
    liquid: Math.round(clampNumber(source.liquid, 0, 100, paintSettings.liquid ?? DEFAULT_PAINT_SETTINGS.liquid)),
    stickiness: Math.round(clampNumber(source.stickiness, 0, 100, paintSettings.stickiness || DEFAULT_PAINT_SETTINGS.stickiness)),
    gloss: Math.round(clampNumber(source.gloss, 0, 100, paintSettings.gloss || DEFAULT_PAINT_SETTINGS.gloss)),
    lightAngle: Math.round(clampNumber(source.lightAngle, 0, 360, paintSettings.lightAngle || DEFAULT_PAINT_SETTINGS.lightAngle)),
    lightElevation: Math.round(clampNumber(source.lightElevation, 1, 89, paintSettings.lightElevation || DEFAULT_PAINT_SETTINGS.lightElevation)),
    // Physical paint engine section (medium, brush, pigment, surface); unused by the original oil backend.
    physical: normalisePhysicalPaintSettings(source.physical, paintSettings.physical),
  };
}

export function setPaintSettings(value = {}) {
  paintSettings = normalisePaintSettings({ ...paintSettings, ...(value || {}) });
  paintDebugViewMode = normalisePaintDebugViewMode(paintSettings.view);
  return getPaintSettings();
}

export function getPaintSettings() {
  updateLoadingState();
  return {
    ...paintSettings,
    view: normalisePaintDebugViewMode(paintDebugViewMode),
    brushLoad: brushLoadVolume,
    brushLoadColor,
    brushLoadRevision,
  };
}

export function setPaintDebugViewMode(value) {
  paintDebugViewMode = normalisePaintDebugViewMode(value);
  paintSettings = { ...paintSettings, view: paintDebugViewMode };
}

export function getPaintDebugViewMode() {
  return paintDebugViewMode;
}

export function setPaintBrushLoading(value) {
  updateLoadingState();
  brushLoading = Boolean(value);
  if (brushLoading) {
    brushLoadColor = paintSettings.paintColor;
    brushLoadRevision += 1;
  }
  brushLoadingStartedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
}

export function cleanPaintBrushLoad() {
  brushLoadVolume = 0;
  brushLoadRevision += 1;
  brushLoading = false;
}

export function getPaintBrushLoadInfo() {
  updateLoadingState();
  return {
    loading: brushLoading,
    volume: brushLoadVolume,
    capacity: 1,
    percent: brushLoadVolume * 100,
    color: brushLoadColor,
    revision: brushLoadRevision,
  };
}

export function syncPaintBrushLoadInfo(volume, color) {
  brushLoading = false;
  brushLoadVolume = clampNumber(volume, 0, 1, brushLoadVolume);
  if (typeof color === 'string' && color) {
    brushLoadColor = normaliseHexColor(color, brushLoadColor);
  }
  brushLoadRevision += 1;
  return getPaintBrushLoadInfo();
}


export const PAINT_BRUSH_CHAIN_VERTEX_COUNT = 10;

export function normalisePaintBrushDirection(direction, fallback = [1, 0]) {
  const source = Array.isArray(direction)
    ? direction
    : direction && typeof direction === 'object'
      ? [direction.x, direction.y]
      : fallback;
  const x = Number(source?.[0]);
  const y = Number(source?.[1]);
  const length = Math.hypot(x, y);

  if (length > 0.0001) return [x / length, y / length];

  const fallbackX = Number(Array.isArray(fallback) ? fallback[0] : fallback?.x) || 1;
  const fallbackY = Number(Array.isArray(fallback) ? fallback[1] : fallback?.y) || 0;
  const fallbackLength = Math.hypot(fallbackX, fallbackY) || 1;
  return [fallbackX / fallbackLength, fallbackY / fallbackLength];
}

export function getPaintBrushBristleOffset(sourceIndex, bristleCount, brushRadius) {
  const safeIndex = Math.max(0, Number(sourceIndex) || 0);
  const safeCount = Math.max(1, Number(bristleCount) || 1);
  const safeRadius = Math.max(0, Number(brushRadius) || 0);
  const seed = Math.sin((safeIndex + 1) * 12.9898) * 43758.5453;
  const seed01 = seed - Math.floor(seed);
  const radial = Math.sqrt((safeIndex + 0.5) / safeCount);
  const angle = safeIndex * 2.399963229728653 + seed01 * 0.9;
  const rootRadius = safeRadius * 0.88 * radial;

  return {
    x: Math.cos(angle) * rootRadius,
    y: Math.sin(angle) * rootRadius,
  };
}

export function createPaintBrushCursorBristles(point, direction, settings = {}) {
  if (!point) return [];

  const brushSize = Math.max(4, Number(settings.toolSize) || 1);
  const brushRadius = brushSize * 0.5;
  const bristleCount = Math.max(1, Math.min(250, Math.round(Number(settings.bristleCount) || 32)));
  const bristleLength = Math.max(1, Math.min(300, Number(settings.bristleLength) || 40));
  const segmentCount = PAINT_BRUSH_CHAIN_VERTEX_COUNT - 1;
  const segmentLength = bristleLength / segmentCount;
  const safeDirection = normalisePaintBrushDirection(direction, [1, 0]);

  return Array.from({ length: bristleCount }, (_, sourceIndex) => {
    const offset = getPaintBrushBristleOffset(sourceIndex, bristleCount, brushRadius);
    const baseX = Number(point.x) + offset.x;
    const baseY = Number(point.y) + offset.y;

    return {
      sourceIndex,
      nodes: Array.from({ length: PAINT_BRUSH_CHAIN_VERTEX_COUNT }, (_, nodeIndex) => ({
        x: baseX + safeDirection[0] * segmentLength * nodeIndex,
        y: baseY + safeDirection[1] * segmentLength * nodeIndex,
      })),
    };
  });
}

export function advancePaintBrushCursorBristles(
  previousBristles,
  point,
  previousPoint,
  settings = {},
  previousDirection = [1, 0],
) {
  if (!point) return [];

  const brushSize = Math.max(4, Number(settings.toolSize) || 1);
  const brushRadius = brushSize * 0.5;
  const bristleCount = Math.max(1, Math.min(250, Math.round(Number(settings.bristleCount) || 32)));
  const bristleLength = Math.max(1, Math.min(300, Number(settings.bristleLength) || 40));
  const segmentLength = bristleLength / (PAINT_BRUSH_CHAIN_VERTEX_COUNT - 1);
  let direction = normalisePaintBrushDirection(previousDirection, [1, 0]);

  if (previousPoint) {
    const deltaX = Number(point.x) - Number(previousPoint.x);
    const deltaY = Number(point.y) - Number(previousPoint.y);
    const distance = Math.hypot(deltaX, deltaY);
    if (distance > 0.15) direction = [deltaX / distance, deltaY / distance];
  }

  const mustReset = !Array.isArray(previousBristles)
    || previousBristles.length !== bristleCount
    || previousBristles.some((bristle, index) => (
      !bristle
      || Number(bristle.sourceIndex) !== index
      || !Array.isArray(bristle.nodes)
      || bristle.nodes.length !== PAINT_BRUSH_CHAIN_VERTEX_COUNT
    ));

  if (mustReset) return createPaintBrushCursorBristles(point, direction, settings);

  return previousBristles.map((bristle, sourceIndex) => {
    const offset = getPaintBrushBristleOffset(sourceIndex, bristleCount, brushRadius);
    const nodes = bristle.nodes.map((node) => ({
      x: Number(node?.x) || 0,
      y: Number(node?.y) || 0,
    }));

    nodes[0] = {
      x: Number(point.x) + offset.x,
      y: Number(point.y) + offset.y,
    };

    for (let pass = 0; pass < 3; pass += 1) {
      for (let nodeIndex = 1; nodeIndex < nodes.length; nodeIndex += 1) {
        const leader = nodes[nodeIndex - 1];
        const follower = nodes[nodeIndex];
        const deltaX = follower.x - leader.x;
        const deltaY = follower.y - leader.y;
        const distance = Math.hypot(deltaX, deltaY);

        if (distance <= 0.0001) {
          follower.x = leader.x + direction[0] * segmentLength;
          follower.y = leader.y + direction[1] * segmentLength;
          continue;
        }

        follower.x = leader.x + (deltaX / distance) * segmentLength;
        follower.y = leader.y + (deltaY / distance) * segmentLength;
      }
    }

    return { sourceIndex, nodes };
  });
}

// Pen input (raw pressure, pointer type, tilt, twist) carried to resampled
// points for the physical paint engine. Kept apart from `pressure`, which the
// original oil renderer reads, so its behaviour is unchanged.
const PAINT_INPUT_KEYS = ['inputPressure', 'tiltX', 'tiltY', 'twist'];

function paintInputOf(point) {
  const raw = Number(point && point.inputPressure !== undefined ? point.inputPressure : point && point.pressure);
  return {
    inputPressure: Number.isFinite(raw) ? raw : undefined,
    tiltX: Number(point && point.tiltX),
    tiltY: Number(point && point.tiltY),
    twist: Number(point && point.twist),
  };
}

function blendPaintInput(target, inputs, weights, pointerType) {
  for (const key of PAINT_INPUT_KEYS) {
    let sum = 0;
    let ok = true;
    inputs.forEach((input, index) => {
      if (!Number.isFinite(input[key])) ok = false;
      else sum += input[key] * weights[index];
    });
    if (ok) target[key] = sum;
  }
  if (pointerType) target.pointerType = pointerType;
  return target;
}

function midpointPaintPoint(a, b) {
  return blendPaintInput({
    x: (Number(a.x) + Number(b.x)) * 0.5,
    y: (Number(a.y) + Number(b.y)) * 0.5,
    pressure: Math.max(Number(a.pressure) || 0, Number(b.pressure) || 0) || undefined,
  }, [paintInputOf(a), paintInputOf(b)], [0.5, 0.5], b.pointerType || a.pointerType);
}

function buildQuadraticPaintPoints(start, control, end, options = {}) {
  const settings = normalisePaintSettings(options.paintSettings || getPaintSettings());
  const radius = Math.max(1, settings.toolSize * 0.5);
  const distance = Math.hypot(Number(end.x) - Number(start.x), Number(end.y) - Number(start.y));
  const steps = Math.max(2, Math.min(64, Math.ceil(distance / Math.max(1, radius * 0.2))));
  const points = [];
  const inputs = [paintInputOf(start), paintInputOf(control), paintInputOf(end)];
  const pointerType = end.pointerType || control.pointerType || start.pointerType;

  for (let index = 0; index <= steps; index += 1) {
    const t = index / steps;
    const inv = 1 - t;
    const weights = [inv * inv, 2 * inv * t, t * t];
    points.push(blendPaintInput({
      x: weights[0] * Number(start.x) + weights[1] * Number(control.x) + weights[2] * Number(end.x),
      y: weights[0] * Number(start.y) + weights[1] * Number(control.y) + weights[2] * Number(end.y),
      pressure: options.pressure,
    }, inputs, weights, pointerType));
  }

  return points;
}

export function drawPaintStrokeWithinSelection(engine, points, options = {}) {
  if (!engine || typeof engine.drawPaintStroke !== 'function') return false;
  const current = getPaintSettings();
  const supplied = options.paintSettings && typeof options.paintSettings === 'object'
    ? options.paintSettings
    : {};
  const settings = {
    ...normalisePaintSettings({ ...current, ...supplied }),
    brushLoad: Number.isFinite(Number(supplied.brushLoad)) ? Number(supplied.brushLoad) : current.brushLoad,
    brushLoadColor: supplied.brushLoadColor || current.brushLoadColor,
    brushLoadRevision: Number.isFinite(Number(supplied.brushLoadRevision)) ? Number(supplied.brushLoadRevision) : current.brushLoadRevision,
  };
  if (settings.tool === 'knife') return false;
  return engine.drawPaintStroke(points, settings, { ...options, paintSettings: settings });
}

export function drawPaintCurveSegmentWithinSelection(engine, previousPreviousPoint, previousPoint, currentPoint, options = {}) {
  if (!engine || !previousPreviousPoint || !previousPoint || !currentPoint) return false;
  const curveStart = midpointPaintPoint(previousPreviousPoint, previousPoint);
  const curveEnd = midpointPaintPoint(previousPoint, currentPoint);
  return drawPaintStrokeWithinSelection(engine, buildQuadraticPaintPoints(curveStart, previousPoint, curveEnd, options), options);
}

export function drawPaintFinalTailWithinSelection(engine, previousPoint, currentPoint, options = {}) {
  if (!engine || !previousPoint || !currentPoint) return false;
  const tailStart = midpointPaintPoint(previousPoint, currentPoint);
  return drawPaintStrokeWithinSelection(engine, [tailStart, currentPoint], options);
}

export function drawPaintSegmentWithinSelection(engine, previousPoint, currentPoint, options = {}) {
  if (!engine || !previousPoint) return false;
  return drawPaintStrokeWithinSelection(engine, currentPoint ? [previousPoint, currentPoint] : [previousPoint], options);
}

export { PAINT_HEIGHT_PHASE_COUNT, PAINT_HEIGHT_MAX_UNITS, PAINT_HEIGHT_UNITS_PER_PIXEL };

